// src/components/communication/ChatThreadList.jsx
import React, { useState } from 'react';
import Button from '../common/Button';

const ChatThreadList = ({ 
  threads, 
  activeThread, 
  onThreadSelect, 
  loading, 
  error, 
  hasMore, 
  onLoadMore 
}) => {
  const [searchInput, setSearchInput] = useState('');
  
  // Format date
  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    
    // If already a string, return as is
    if (typeof timestamp === 'string') return timestamp;
    
    // Convert Firestore Timestamp to JavaScript Date
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    
    // If today, return time
    const today = new Date();
    if (date.toDateString() === today.toDateString()) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    
    // If this year, return month and day
    if (date.getFullYear() === today.getFullYear()) {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
    
    // Otherwise, return full date
    return date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
  };
  
  // Filter threads by search input
  const filteredThreads = threads.filter(thread => {
    if (!searchInput) return true;
    
    const searchLower = searchInput.toLowerCase();
    return (
      (thread.customerName && thread.customerName.toLowerCase().includes(searchLower)) ||
      (thread.lastMessage && thread.lastMessage.toLowerCase().includes(searchLower))
    );
  });
  
  return (
    <div className="h-full flex flex-col">
      {/* Search */}
      <div className="p-4 border-b border-gray-200">
        <div className="relative rounded-md shadow-sm">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
            </svg>
          </div>
          <input
            type="text"
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
            placeholder="Search conversations..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>
      </div>
      
      {/* Thread List */}
      <div className="flex-1 overflow-y-auto">
        {loading && threads.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
            <p className="mt-2 text-sm text-gray-500">Loading conversations...</p>
          </div>
        ) : error ? (
          <div className="p-4 text-red-700 bg-red-100 border border-red-400 rounded m-4">
            {error}
          </div>
        ) : filteredThreads.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            <p className="mt-2 text-sm text-gray-500">
              {searchInput ? 'No conversations match your search' : 'No conversations yet'}
            </p>
          </div>
        ) : (
          <ul>
            {filteredThreads.map(thread => (
              <li 
                key={thread.id}
                className={`
                  cursor-pointer transition duration-150
                  ${activeThread?.id === thread.id 
                    ? 'bg-orange-50 border-l-4 border-orange-500' 
                    : 'hover:bg-gray-50 border-l-4 border-transparent'}
                `}
                onClick={() => onThreadSelect(thread)}
              >
                <div className="px-4 py-4 sm:px-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        {thread.customerAvatar ? (
                          <img 
                            className="h-10 w-10 rounded-full" 
                            src={thread.customerAvatar} 
                            alt={thread.customerName || 'Customer'} 
                          />
                        ) : (
                          <div className="h-10 w-10 rounded-full bg-orange-100 flex items-center justify-center">
                            <span className="text-orange-600 font-medium text-sm">
                              {thread.customerName?.charAt(0).toUpperCase() || 'C'}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="ml-3 flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {thread.customerName || 'Unknown Customer'}
                          </p>
                          <p className="text-xs text-gray-500">
                            {formatDate(thread.lastMessageTimestamp || thread.updatedAt)}
                          </p>
                        </div>
                        <div className="flex items-center mt-1">
                          <p className={`text-sm truncate ${thread.unreadVendor ? 'font-semibold text-gray-900' : 'text-gray-500'}`}>
                            {thread.lastMessageSender === thread.vendorId ? 'You: ' : ''}
                            {thread.lastMessage || 'No messages yet'}
                          </p>
                          {thread.unreadVendor && (
                            <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                              New
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
      
      {/* Load More Button */}
      {hasMore && filteredThreads.length > 0 && (
        <div className="p-4 border-t border-gray-200">
          <Button
            type="button"
            variant="secondary"
            onClick={onLoadMore}
            className="w-full"
            disabled={loading}
          >
            {loading ? 'Loading...' : 'Load More'}
          </Button>
        </div>
      )}
    </div>
  );
};

export default ChatThreadList;